import {
  FC,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  FormEvent,
} from "react";
import { FormModal } from "../Modal";
import { HorizontalStack, VerticalStack } from "../../Stack";
import { BtFileObject, Document } from "../../../models";
import { useTranslation } from "react-i18next";
import {
  FontIcon,
  Label,
  Link,
  mergeStyles,
  Stack,
  Text,
  TextField,
  TooltipHost,
  ITooltipHostStyles,
} from "@fluentui/react";
import { useDropzone } from "react-dropzone";
import Loader from "../../Loader";
import {
  createTemplate,
  createTemplateVersion,
  getPrecedentDocuments,
} from "../../../modules/redux/Documents/document";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  getAllProjectDetails,
  getAllProjects,
} from "../../../modules/redux/Projects/project";
import { TextForBody, TextForPageHeaders } from "../../Text";
import DocumentList from "../../Cards/DocumentList";
import { colorPallete } from "../../../assets/color";
import UploadDocumentIcon from "../../../assets/Icons/svg/uploadDocumentIcon";
import CustomDropdown from "../../CustomDropDown";
import WordDocIcon from "../../../assets/Icons/svg/wordDocIcon";
import "./index.css";
import AddNewVersion from "./components/addNewVersion";
import { getSortedMRU } from "../../../utils";
import PdfIcon from "../../../assets/Icons/svg/pdfIcon";
import { authService } from "../../../services/AuthService";
import { ProjectService } from "../../../services/ProjectService";
import useIsSignedIn from "../../../hooks/useIsSignedIn";
import { ApiClient } from "../../../network/ApiClient";
import { ApiEndpoints } from "../../../network/ApiEndpoints";
import {
  DocumentService,
  FileRegistrationRequest,
} from "../../../services/DocumentService";
import { uploadProgressHub } from "../../../services/UploadProgressHub";
import { useUploadNotification, FileVersionInfo } from "../../../context/UploadNotificationContext";
import { useGlobalDragDrop } from "../../DragAndDrop/useGlobalDragDrop";
import { DragOverlay } from "../../DragAndDrop/DragOverlay";
import SuccessIcon from "../../../assets/Icons/svg/successIcon";
import { findDuplicateFileNames } from "./duplicateFileNames";
import DuplicateNameError from "./components/duplicateNameError";

const tooltipHostStyles: Partial<ITooltipHostStyles> = {
  root: {
    display: 'inline-block',
    cursor: 'pointer',
    width: '100%'
  }
};

const isCompletedStage = (stage: string) => {
  const v = (stage || "").toLowerCase().trim();
  return v.includes("complete") || v.includes("success") || v === "done";
};

const isActiveStage = (stage: string) => {
  const v = (stage || "").toLowerCase().trim();
  return !isCompletedStage(stage) && v !== "cancelled" && v !== "error";
};

export const CreateDocumentModal: FC<any> = (props) => {
  const [folderName, setFolderName] = useState("");
  const [fileNameError, setFileNameError] = useState(false);
  const [fileNameErrorMessage, setFileNameErrorMessage] = useState<string>("");
  const [modal, setModal] = useState<boolean>(false);
  const [upload, setUpload] = useState<boolean>(false);
  const [msg, setMsg] = useState<string>("");
  const [showResponse, setShowResponse] = useState<boolean>(false);
  const [responseData, setResponseData] = useState<any>([]);
  const [nameError, setNameError] = useState(false);
  const [newVersion, setNewVersion] = useState(false);
  const [addDocument, setAddDocument] = useState(false);
  const [error1, setError] = useState(false);
  const [folderNameError, setFolderNameError] = useState<boolean>(false);
  const [projectData, setProjectData] = useState<any>([]);
  const [docsData, setDocsData] = useState<Document[]>([]);
  const [documentName, setDocumentName] = useState<string>("");
  const [projectLimit, setProjectLimit] = useState<number>(0);
  const [documentNameError, setDocumentNameError] = useState<boolean>(false);
  const [rootDocumentId, setRootDocumentId] = useState(0);
  const [projectId, setProjectId] = useState(0);
  const [isUploadingProcess, setIsUploadingProcess] = useState(false);
  const [multipleFilesError, setMultipleFilesError] = useState<boolean>(false);
  const [documentItem, setDocumentItem] = useState<Document | any>();
  const [newFileName, setNewFileName] = useState(
    props.document?.rootDocumentName
  );
  const [invalidFiles, setInvalidFiles] = useState<{ [key: string]: boolean }>({});
  const isSignedIn = useIsSignedIn();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const {
    setUploads,
    setIsMinimized,
    addCompletedNotification,
    setUploadRoute,
    operationIds: contextOperationIds,
    setOperationIds: setContextOperationIds,
    setFileNameMap: setContextFileNameMap,
    uploads: contextUploads,
    fileNameMap: contextFileNameMap,
    fileVersionMap: contextFileVersionMap,
    setFileVersionMap: setContextFileVersionMap,
  } = useUploadNotification();

  const fetchProjectsLimits = async () => {
    try {
      const limit: any = await ProjectService.getProjectLimit();
      const value = limit?.data?.[0]?.value;
      if (value !== undefined && value !== null) {
        setProjectLimit(value);
      }
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  };

  useEffect(() => {
    if (isSignedIn) {
      fetchProjectsLimits();
    }
  }, [isSignedIn]);

  useEffect(() => {
    setRootDocumentId(props?.rootDocumentId);
    if (props?.rootDocumentId && props?.rootDocumentId !== 0) {
      setNewVersion(true);
    }
  }, [props?.rootDocumentId]);

  const { data, metadata } = useAppSelector(
    (state: any) => state.fetchProjects
  );

  const [singleFileError, setSingleFileError] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isCompleteApiDone, setIsCompleteApiDone] = useState(false);
  const [activeUploads, setActiveUploads] = useState<
    { FileOperationId: string; Percent: number; Stage: string }[]
  >([]);
  const hasProcessedSuccess = useRef(false);
  useEffect(() => {
    if (data && data.length > 0) {
      setProjectData(data);
    } else {
      setProjectData([]);
    }
  }, [data, metadata]);

  useEffect(() => {
    if (props?.folderName) setFolderName(props.folderName);
    if (props?.projectId) setProjectId(props.projectId);
  }, [props.folderName, props.projectId]);

  useEffect(() => {
    if (documentItem?.rootDocumentId) {
      setRootDocumentId(documentItem.rootDocumentId);
    }
  }, [documentItem]);

  useEffect(() => {
    if (isUploadingProcess) return;

    const selectedProject = projectData.find(
      (item: any) => item.name === folderName
    );

    if (selectedProject?.id) {
      setProjectId(selectedProject.id);
      // DEX-573: only fetch the project's document list when the modal is actually open.
      // This modal is mounted (closed) on the document page, so fetching here duplicated
      // the page's own GET /documents/{projectId}/root. It renders nothing until opened,
      // and re-runs (isModalOpen dep) to fetch fresh data on open.
      if (props.isModalOpen) {
        getSelectedProjectDocuments(selectedProject.name);
      }
    }
  }, [folderName, projectData, props.isModalOpen]);

  const getSelectedProjectDocuments = async (folderName: string) => {
    const selectedProject = projectData.find(
      (item: any) => item.name === folderName
    );

    if (!selectedProject?.id) {
      setDocsData([]);
      return;
    }

    try {
      const response = await ApiClient.get<any>(
        ApiEndpoints.getProjectDocuments(selectedProject.id),
        {},
        {}
      );

      setDocumentItem(null);

      let documents = [];

      if (response.data?.data) {
        if (Array.isArray(response.data.data)) {
          documents = response.data.data;
        } else if (response.data.data?.Items && Array.isArray(response.data.data.Items)) {
          documents = response.data.data.Items;
        } else if (typeof response.data.data === 'object') {
          documents = response.data.data.items || response.data.data.data || [];
        }
      } else if (response.data?.documents && Array.isArray(response.data.documents)) {
        documents = response.data.documents;
      }

      if (documents.length > 0) {
        // DEX-573 M4: the /root response already carries each doc's latest version
        // (M3 enrichment), so read doc.latestVersion instead of firing a per-document
        // /versions call for every row.
        const enrichedDocuments: any[] = documents.map((doc: any) => {
          const latestVersion = doc.latestVersion;
          return {
            ...doc,
            version: latestVersion?.version,
            lastUpdatedDate: latestVersion?.lastUpdatedDate ?? doc.lastActivityDate,
            fileType: latestVersion?.fileType,
            metadata: latestVersion?.metadata,
          };
        });
        const sortedDocs = getSortedMRU(enrichedDocuments);
        setDocsData(sortedDocs);
      } else {
        setDocsData([]);
      }
    } catch (error) {
      console.error(error);
      setDocsData([]);
    }
  };

  useEffect(() => {
    if (nameError && props.multipleFiles) {
      let check: boolean = false;
      props.multipleFiles.forEach((btFile: BtFileObject) => {
        if (btFile.fileName.length === 0) check = true;
      });
      setNameError(check);
    }
    if (props.multipleFiles && props.multipleFiles.length > 0) {
      setMultipleFilesError(false);
      setError(false);
      setSingleFileError(false);
    }
  }, [props.multipleFiles, nameError]);

  const handleFileTypeChange = (
    fileName: string,
    isNewVersion: boolean,
    rootDocumentId?: number,
    documentFamilyName?: string
  ) => {
    if (isNewVersion && rootDocumentId) {
      setRootDocumentId(rootDocumentId);
      setDocumentName(documentFamilyName || "");
    }
  };

  const redirectFolder = (id: number | undefined) => {
    if (props?.projectId && props?.projectId === id) {
      window.location.reload();
    } else {
      window.location.href = `/home/${id}`;
    }
  };

  const lowercaseAfterLastDot = (text: string) => {
    const lastDotIndex = text.lastIndexOf(".");
    if (lastDotIndex === -1 || lastDotIndex === text.length - 1) {
      return text;
    }

    const beforeDot = text.slice(0, lastDotIndex + 1);
    const afterDot = text.slice(lastDotIndex + 1);

    return beforeDot + afterDot.toLowerCase();
  };

  // Opened from a document's import icon (as opposed to the project's generic
  // import): that click hands us the document family, so anything dropped
  // while the modal is open defaults to a new version of it.
  const targetRootDocumentId: number = props?.rootDocumentId || 0;
  const isDocumentImport = !props.copy && targetRootDocumentId > 0;
  const targetDocumentFamilyName = useMemo(() => {
    if (!targetRootDocumentId) return "";
    const target = docsData.find(
      (doc: any) => doc?.rootDocumentId === targetRootDocumentId
    );
    return (target as any)?.rootDocumentName?.split(".docx")[0] ?? "";
  }, [docsData, targetRootDocumentId]);

  // Files dropped on the page are handled by DragAndDrop's own drop handler
  // (it sits above this modal on the drag-drop bus), so it builds the file
  // objects without the target. Stamp the default here on the list instead of
  // only where the files are created, so every entry point behaves the same.
  useEffect(() => {
    if (!isDocumentImport || !props.isModalOpen || !props.setMultipleFiles) {
      return;
    }
    const files: BtFileObject[] = props.multipleFiles ?? [];
    // `isNewVersion === undefined` means the user hasn't touched that file's
    // toggle yet — an explicit `false` is a choice and must be preserved.
    const needsTarget = (file: BtFileObject) =>
      file.isNewVersion === undefined ||
      (file.isNewVersion === true &&
        file.rootDocumentId === targetRootDocumentId &&
        !file.documentFamilyName &&
        !!targetDocumentFamilyName);
    if (!files.some(needsTarget)) return;
    props.setMultipleFiles(
      files.map((file: BtFileObject) =>
        needsTarget(file)
          ? {
              ...file,
              isNewVersion: true,
              rootDocumentId: targetRootDocumentId,
              documentFamilyName: targetDocumentFamilyName,
            }
          : file
      )
    );
  }, [
    isDocumentImport,
    props.isModalOpen,
    props.multipleFiles,
    targetRootDocumentId,
    targetDocumentFamilyName,
  ]);

  function addMultipleFiles(files: File[]) {
    setFileNameError(false);
    let incomingFiles: BtFileObject[] = [];
    files.forEach((fObject: File) => {
      incomingFiles.push({
        fileName: lowercaseAfterLastDot(fObject.name),
        file: fObject,
        ...(isDocumentImport
          ? {
              isNewVersion: true,
              rootDocumentId: targetRootDocumentId,
              documentFamilyName: targetDocumentFamilyName,
            }
          : {}),
      });
    });
    props.setMultipleFiles((prevFiles: BtFileObject[]) => {
      let result = [...prevFiles, ...incomingFiles];
      const uniqueArr = result.filter(
        (value, index, array) =>
          array.findIndex((curr) => value.fileName === curr.fileName) === index
      );
      return uniqueArr;
    });
    setMultipleFilesError(false);
    setSingleFileError(false);
  }

  const abortControllerRef = useRef<Map<string, AbortController>>(new Map());
  const hasTriggeredUploadCloseRef = useRef(false);
  const fileNameMapRef = useRef<Record<string, string>>({});
  // Per-operation version info. Kept in sync with the upload context so it can be
  // rebuilt on the minimize/restore path — otherwise a restored new-version upload
  // would be treated as a new document and spawn a ghost placeholder card.
  const fileVersionInfoRef = useRef<Record<string, FileVersionInfo>>({});
  const unsubscribeHubRef = useRef<(() => void) | null>(null);
  const uploadQueueRef = useRef<Record<string, Array<{ Percent: number; Stage: string }>>>({});
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hubFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const drainQueue = useCallback(() => {
    animationTimerRef.current = null;
    let hasMore = false;
    setActiveUploads((prev) =>
      prev.map((u) => {
        const q = uploadQueueRef.current[u.FileOperationId];
        if (!q || q.length === 0) return u;
        const next = q.shift()!;
        if (q.length > 0) hasMore = true;
        return { ...u, Percent: next.Percent, Stage: next.Stage || u.Stage };
      })
    );
    if (hasMore) {
      animationTimerRef.current = setTimeout(drainQueue, 350);
    }
  }, []);

  useEffect(() => {
    if (activeUploads.length > 0) {
      setUploads(
        activeUploads.map((u) => ({
          fileOperationId: u.FileOperationId,
          fileName: fileNameMapRef.current[u.FileOperationId] ?? u.FileOperationId,
          percent: u.Percent,
          stage: u.Stage,
          projectId: projectId,
          projectRoute: `/home/${projectId}`,
        }))
      );
    }
  }, [activeUploads, projectId]);

  const isUploadActiveRef = useRef(false);
  useEffect(() => {
    isUploadActiveRef.current =
      modal && !upload && (isUploadingProcess || isValidating || activeUploads.length > 0);
  }, [modal, upload, isUploadingProcess, isValidating, activeUploads]);

  useEffect(() => {
    return () => {
      if (isUploadActiveRef.current) setIsMinimized(true);
    };
  }, []);

  useEffect(() => {
    if (!props.isModalOpen || modal || contextUploads.length === 0) return;
    hasTriggeredUploadCloseRef.current = false;
    fileNameMapRef.current = { ...contextFileNameMap };
    fileVersionInfoRef.current = { ...contextFileVersionMap };
    uploadQueueRef.current = {};
    setModal(true);
    setIsMinimized(false);
    // Restored uploads are already past register/complete, so minimize must stay available.
    setIsCompleteApiDone(true);
    setActiveUploads(
      contextUploads.map((u) => ({
        FileOperationId: u.fileOperationId,
        Percent: u.percent,
        Stage: u.stage,
      }))
    );
    const recoveryIds = new Set(contextUploads.map((u) => u.fileOperationId));
    unsubscribeHubRef.current?.();
    unsubscribeHubRef.current = uploadProgressHub.subscribe((update) => {
      const q = uploadQueueRef.current;
      if (!q[update.fileOperationId]) q[update.fileOperationId] = [];
      q[update.fileOperationId].push({
        Percent: update.progress ?? 0,
        Stage: update.status ?? "",
      });
      if (!animationTimerRef.current) {
        animationTimerRef.current = setTimeout(drainQueue, 0);
      }
    }, recoveryIds);
  }, [props.isModalOpen]);

  useEffect(() => {
    if (!modal || !activeUploads.length || hasTriggeredUploadCloseRef.current) return;

    const nonCancelled = activeUploads.filter(
      (u) => u.Stage !== "cancelled" && u.Stage !== "error"
    );
    if (!nonCancelled.length) return;
    if (!nonCancelled.every((u) => isCompletedStage(u.Stage) || u.Percent >= 100)) return;

    hasTriggeredUploadCloseRef.current = true;

    const uploadedFileNames = nonCancelled
      .map((u) => fileNameMapRef.current[u.FileOperationId])
      .filter(Boolean) as string[];

    const newDocumentFileNames = nonCancelled
      .filter((u) => !fileVersionInfoRef.current[u.FileOperationId]?.isNewVersion)
      .map((u) => fileNameMapRef.current[u.FileOperationId])
      .filter(Boolean) as string[];

    const newVersionRootDocIds = Array.from(
      new Set(
        nonCancelled
          .map((u) => fileVersionInfoRef.current[u.FileOperationId]?.rootDocumentId)
          .filter((id): id is number => !!id)
      )
    );

    successTimerRef.current = setTimeout(() => {
      successTimerRef.current = null;
      setMsg("Success");
      setUpload(true);
      setShowResponse(true);
      setResponseData([{ code: 200, message: "Uploaded Successfully" }]);
      dispatch(getPrecedentDocuments({ limit: 21, offset: 0 }));
      dispatch(getAllProjects({ limit: 30, offset: 0 }));
      addCompletedNotification(uploadedFileNames);
      props.onUploadSuccess?.(newDocumentFileNames, newVersionRootDocIds);
    }, 1000);

    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      disable();
    }, 3000);
  }, [activeUploads, modal, projectId]);

  // Safety net for a dropped terminal SignalR event: SignalR is primary, but if it is
  // missed the bar would stick below 100%. Poll the authoritative upload status for any
  // non-terminal upload and apply it — Completed drives to 100 (the effect above then
  // resolves), Failed/Cancelled mark the row. (upload-progress-reconcile M2 / D1, D3.)
  const activeUploadsRef = useRef(activeUploads);
  useEffect(() => {
    activeUploadsRef.current = activeUploads;
  }, [activeUploads]);

  useEffect(() => {
    if (!modal) return;
    const interval = setInterval(async () => {
      const pending = activeUploadsRef.current.filter(
        (u) =>
          u.Percent < 100 &&
          u.Stage !== "complete" &&
          u.Stage !== "error" &&
          u.Stage !== "cancelled" &&
          !isCompletedStage(u.Stage)
      );
      if (pending.length === 0) return;
      try {
        const res: any = await DocumentService.getUploadStatuses(
          pending.map((u) => u.FileOperationId)
        );
        const statuses: any[] = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
        if (statuses.length === 0) return;
        const statusById: Record<string, string> = {};
        statuses.forEach((s: any) => {
          const id = s.fileOperationId ?? s.FileOperationId;
          if (id) statusById[id] = (s.status ?? s.Status ?? "").toString();
        });
        setActiveUploads((prev) =>
          prev.map((u) => {
            const st = statusById[u.FileOperationId];
            if (!st) return u;
            if (st === "Completed") return { ...u, Percent: 100, Stage: "complete" };
            if (st === "Failed") return { ...u, Stage: "error" };
            if (st === "Cancelled") return { ...u, Stage: "cancelled" };
            return u;
          })
        );
      } catch (err) {
        console.warn("[UploadReconcile] status poll failed:", err);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [modal]);

  const registerAndUpload = async (): Promise<string[] | null> => {
    if (!projectId || !props.multipleFiles?.length) return null;

    const getFileExtension = (name: string): string => {
      const parts = name.split(".");
      if (parts.length < 2) return "";
      return parts.pop()?.toLowerCase() || "";
    };

    const fileByOperationId: Record<string, File> = {};
    const fileNameByOperationId: Record<string, string> = {};
    const fileVersionInfoByOperationId: Record<string, FileVersionInfo> = {};

    type FileMeta = {
      fileObj: BtFileObject;
      fileOperationId: string;
      groupKey: string;
    };
    const fileMetas: FileMeta[] = props.multipleFiles.map((fileObj: BtFileObject) => {
      const fileOperationId = crypto.randomUUID();
      fileByOperationId[fileOperationId] = fileObj.file;
      fileNameByOperationId[fileOperationId] = fileObj.fileName;
      fileVersionInfoByOperationId[fileOperationId] = {
        isNewVersion: !!fileObj.isNewVersion,
        ...(fileObj.isNewVersion && fileObj.rootDocumentId
          ? { rootDocumentId: fileObj.rootDocumentId }
          : {}),
      };
      const groupKey = fileObj.isNewVersion
        ? `version-${fileObj.rootDocumentId}`
        : "new-doc";
      return { fileObj, fileOperationId, groupKey };
    });
    fileNameMapRef.current = fileNameByOperationId;
    fileVersionInfoRef.current = fileVersionInfoByOperationId;

    setIsValidating(true);
    setUpload(false);
    setMsg("uploading");
    abortControllerRef.current = new Map();

    // Group files so each registration call carries a uniform rootDocumentId.
    // This preserves the existing batch-level API contract while supporting
    // multiple different new-version targets in a single import.
    const groups = new Map<string, FileMeta[]>();
    fileMetas.forEach((meta) => {
      if (!groups.has(meta.groupKey)) groups.set(meta.groupKey, []);
      groups.get(meta.groupKey)!.push(meta);
    });

    const groupRegistrationResults = await Promise.allSettled(
      Array.from(groups.entries()).map(([key, metas]) => {
        const isVersion = key.startsWith("version-");
        const rootDocId = isVersion ? metas[0].fileObj.rootDocumentId : undefined;
        const payload: FileRegistrationRequest[] = metas.map(({ fileObj, fileOperationId }) => ({
          fileOperationId,
          fileName: fileObj.fileName,
          fileSize: fileObj.file.size,
          fileFormat: getFileExtension(fileObj.fileName),
        }));
        return DocumentService.registerUpload(
          payload,
          projectId,
          rootDocId,
          isVersion ? true : undefined,
        );
      })
    );

    const registrations = groupRegistrationResults.flatMap((r) =>
      r.status === "fulfilled" ? r.value : []
    );

    if (!registrations?.length) {
      throw new Error("Registration returned no files");
    }

    const operationIds = registrations.map((r) => r.fileOperationId).filter(Boolean) as string[];

    setUploadRoute(`/home/${projectId}`);
    setContextOperationIds(operationIds);
    setContextFileNameMap(fileNameByOperationId);
    setContextFileVersionMap(fileVersionInfoByOperationId);

    let hubConnected = false;
    try {
      const token = await authService.getAccessToken();
      await uploadProgressHub.connect(process.env.REACT_APP_BASE_URL || "", token);
      await uploadProgressHub.joinGroups(operationIds);
      hubConnected = true;
    } catch (signalRError) {
      console.error("SignalR setup failed, continuing without hub progress", signalRError);
    }

    const validRegistrations = registrations.filter(
      (reg) => !!reg.fileOperationId && !!reg.sasUrl && !reg.errorMessage
    );
    if (!validRegistrations.length) {
      throw new Error("No valid SAS URLs returned from registration");
    }

    validRegistrations.forEach((reg) => {
      abortControllerRef.current?.set(reg.fileOperationId, new AbortController());
    });

    setActiveUploads(
      registrations
        .filter((r) => !!r.fileOperationId)
        .map((r) => ({
          FileOperationId: r.fileOperationId,
          Percent: 0,
          Stage: "uploading",
        }))
    );
    setIsValidating(false);

    const uploadIds = new Set(validRegistrations.map((r) => r.fileOperationId));
    uploadQueueRef.current = {};
    unsubscribeHubRef.current?.();
    unsubscribeHubRef.current = uploadProgressHub.subscribe((update) => {
      const q = uploadQueueRef.current;
      if (!q[update.fileOperationId]) q[update.fileOperationId] = [];
      q[update.fileOperationId].push({
        Percent: update.progress ?? 0,
        Stage: update.status ?? "",
      });
      if (!animationTimerRef.current) {
        animationTimerRef.current = setTimeout(drainQueue, 0);
      }
    }, uploadIds);

    const sasUploadPromises = validRegistrations.map(async (reg) => {
      const file = fileByOperationId[reg.fileOperationId];
      if (!file || !reg.sasUrl) {
        throw new Error(reg.errorMessage || "Missing SAS URL");
      }
      const abortCtrl = abortControllerRef.current!.get(reg.fileOperationId)!;
      await DocumentService.uploadBlobToSas(
        reg.sasUrl,
        file,
        (percent) => {
          // Show the actual byte-transfer progress for the SAS PUT (the longest
          // phase). Cap at 99 so this never trips the success/auto-close effect,
          // which treats Percent >= 100 as a terminal signal; the "complete"
          // stage comes later from SignalR / completeUpload.
          const capped = Math.min(percent, 99);
          setActiveUploads((prev) =>
            prev.map((u) =>
              u.FileOperationId === reg.fileOperationId &&
                u.Stage === "uploading" &&
                capped > u.Percent
                ? { ...u, Percent: capped }
                : u
            )
          );
        },
        abortCtrl.signal
      );
    });

    const sasResults = await Promise.allSettled(sasUploadPromises);

    const successfulRegs = validRegistrations.filter((_, i) => sasResults[i].status === "fulfilled");
    const failedUploadIds = validRegistrations
      .filter((_, i) => sasResults[i].status === "rejected")
      .map((r) => r.fileOperationId);

    if (failedUploadIds.length > 0) {
      setActiveUploads((prev) =>
        prev.map((u) =>
          failedUploadIds.includes(u.FileOperationId) ? { ...u, Stage: "error" } : u
        )
      );
    }

    if (!successfulRegs.length) {
      throw new Error("All blob uploads failed");
    }

    const validIds = new Set(successfulRegs.map((r) => r.fileOperationId));
    setActiveUploads((prev) =>
      prev.map((u) =>
        validIds.has(u.FileOperationId)
          ? { ...u, Percent: 5, Stage: "Validating documents" }
          : u
      )
    );

    const completeResults = await Promise.allSettled(
      successfulRegs.map((reg) => DocumentService.completeUpload(reg.fileOperationId))
    );

    const failedCompleteIds = successfulRegs
      .filter((_, i) => completeResults[i].status === "rejected")
      .map((r) => r.fileOperationId);

    if (failedCompleteIds.length > 0) {
      setActiveUploads((prev) =>
        prev.map((u) =>
          failedCompleteIds.includes(u.FileOperationId) ? { ...u, Stage: "error" } : u
        )
      );
    }

    setIsCompleteApiDone(true);

    const succeededCompleteIds = successfulRegs
      .filter((_, i) => completeResults[i].status === "fulfilled")
      .map((r) => r.fileOperationId);

    if (succeededCompleteIds.length > 0) {
      if (!hubConnected) {
        // No live progress channel: SignalR never delivers a terminal stage, so
        // the success/auto-close effect would wait forever. completeUpload
        // succeeded, so resolve these optimistically.
        setActiveUploads((prev) =>
          prev.map((u) =>
            succeededCompleteIds.includes(u.FileOperationId)
              ? { ...u, Percent: 100, Stage: "complete" }
              : u
          )
        );
      } else {
        // Hub is connected, but the backend may never emit a terminal event.
        // Fall back after a grace period so the modal can never hang open.
        hubFallbackTimerRef.current = setTimeout(() => {
          hubFallbackTimerRef.current = null;
          setActiveUploads((prev) =>
            prev.map((u) =>
              succeededCompleteIds.includes(u.FileOperationId) &&
                !isCompletedStage(u.Stage)
                ? { ...u, Percent: 100, Stage: "complete" }
                : u
            )
          );
        }, 30000);
      }
    }

    return registrations.map((r) => r.fileOperationId).filter(Boolean) as string[];
  };

  const { isDraggingOver } = useGlobalDragDrop((files) => {
    if (!props.copy) addMultipleFiles(files);
  });

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    noDragEventsBubbling: true,
    multiple: true,
    onDrop: (acceptedFiles) => {
      addMultipleFiles(acceptedFiles);
    },
  });

  const style = useMemo(
    () => ({
      ...(isFocused ? styles.focusedStyle : {}),
      ...(isDragAccept ? styles.acceptStyle : {}),
      ...(isDragReject ? styles.rejectStyle : {}),
      ...{ height: "100%", width: "100%" },
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  function validateFileName() {
    let hasError = false;
    let invalidFiles: { [key: string]: boolean } = {};

    props.multipleFiles.forEach((btFile: BtFileObject) => {
      if (String(btFile.fileName).length > 0) {
        const invalidChars = /[\\/:*?<>|]/;
        if (invalidChars.test(btFile.fileName)) {
          hasError = true;
          invalidFiles[btFile.fileName] = true;
        } else {
          invalidFiles[btFile.fileName] = false;
        }
      }
    });

    if (hasError) {
      setFileNameError(true);
      setFileNameErrorMessage("File name cannot contain any of these characters: \\ / : * ? < > |");
    } else {
      setFileNameError(false);
      setFileNameErrorMessage("");
    }
    return invalidFiles;
  }

  useEffect(() => {
    if (props.multipleFiles && props.multipleFiles.length > 0) {
      const validationResults = validateFileName();
      setInvalidFiles(validationResults);
    }
  }, [props.multipleFiles]);

  // The backend rejects a new document whose name is already taken, but only
  // with free text, which ends up as the generic upload_failed. Catch it here
  // so the user is told why and can rename the file before uploading.
  const duplicateFileNames = useMemo(
    () =>
      props.copy
        ? { inProject: [], inBatch: [] }
        : findDuplicateFileNames(props.multipleFiles, docsData),
    [props.copy, props.multipleFiles, docsData]
  );
  const hasDuplicateFileNames =
    duplicateFileNames.inProject.length > 0 || duplicateFileNames.inBatch.length > 0;

  const invalidOrDuplicateFiles = useMemo(() => {
    const flagged = { ...invalidFiles };
    [...duplicateFileNames.inProject, ...duplicateFileNames.inBatch].forEach((name) => {
      flagged[name] = true;
    });
    return flagged;
  }, [invalidFiles, duplicateFileNames]);

  function checkValidation(value?: any) {
    var check: boolean = false;
    props.multipleFiles.forEach((btFile: BtFileObject) => {
      if (btFile.fileName.length === 0) {
        check = true;
      }
    });
    if (String(folderName).length === 0) {
      setFolderNameError(true);
      return;
    }
    if (newVersion && String(documentName).length === 0) {
      setDocumentNameError(true);
      return;
    }
    const invalidFiles = validateFileName();
    if (Object.values(invalidFiles).some(isInvalid => isInvalid)) {
      setFileNameError(true);
      setError(true);
      setFileNameErrorMessage("File name cannot contain any of these characters: \\ / : * ? < > |");
      return;
    }
    if (props.multipleFiles.length === 0) {
      setError(true);
      return;
    }
    if (check) {
      setNameError(true);
      return;
    }
    if (!props.copy && hasDuplicateFileNames) {
      return;
    }
    if (!error1 && String(folderName).length !== 0 && props.multipleFiles.length > 0) {
      if (!props.copy && newVersion && props.multipleFiles.length === 1 && value !== "confirmed") {
        setAddDocument(true);
        return true;
      }
      return true;
    }
  }

  const copyDocument = () => {
    if (String(folderName).length === 0) {
      setFolderNameError(true);
      return;
    }
    if (newVersion && String(documentName).length === 0) {
      setDocumentNameError(true);
      return;
    }
    if (!newVersion && String(folderName).length !== 0) {
      let obj = {
        projectId: projectId,
        driveItemId: props.document?.metadata?.driveItemId,
        newFileName: newFileName,
      };
      setModal(true);
      dispatch(createTemplate(obj))
        .then((res: any) => {
          setMsg("Success");
          setUpload(true);
          setResponseData([res?.payload]);
          setShowResponse(true);
          setTimeout(() => {
            dispatch(getPrecedentDocuments({ limit: 21, offset: 0 }));
            dispatch(getAllProjects({ limit: 30, offset: 0 }));
            dispatch(getAllProjectDetails(projectId)).then(() => {
              redirectFolder(projectId);
            });
            disable();
          }, 2000);
        })
        .catch((res: any) => {
          setUpload(true);
          res ? setMsg(res) : setMsg("upload_failed");
          setTimeout(() => {
            setUpload(false);
            setModal(false);
            disable();
          }, 2000);
        });
    } else if (
      newVersion &&
      String(folderName).length !== 0 &&
      String(documentName).length !== 0
    ) {
      let obj = {
        rootDocumentId: rootDocumentId,
        driveItemId: props.document?.metadata?.driveItemId,
      };
      setModal(true);
      dispatch(createTemplateVersion(obj))
        .then((res: any) => {
          setMsg("Success");
          setUpload(true);
          setResponseData([res?.payload]);
          setShowResponse(true);
          setTimeout(() => {
            dispatch(getPrecedentDocuments({ limit: 21, offset: 0 }));
            dispatch(getAllProjects({ limit: 30, offset: 0 }));
            dispatch(getAllProjectDetails(projectId)).then(() => {
              redirectFolder(projectId);
            });
            disable();
          }, 2000);
        })
        .catch((res: any) => {
          setUpload(true);
          res ? setMsg(res) : setMsg("upload_failed");
          setTimeout(() => {
            setUpload(false);
            setModal(false);
            disable();
          }, 2000);
        });
    }
  };

  const handleClick = async (data?: string) => {
    if (props?.copy) {
      return copyDocument();
    }

    const isValid = checkValidation();
    const proceed = (!newVersion && isValid) || (newVersion && isValid && data === "confirmed");
    if (!proceed) return;

    setModal(true);
    setError(true);
    hasTriggeredUploadCloseRef.current = false;
    setIsUploadingProcess(true);
    try {
      await registerAndUpload();
    } catch (err) {
      console.error(err);
      setMsg("upload_failed");
      setUpload(true);
    } finally {
      setIsUploadingProcess(false);
      setIsValidating(false);
    }
  };

  const cancelUpload = async (opId?: string) => {
    try {
      const targetIds =
        opId && abortControllerRef.current?.has(opId)
          ? [opId]
          : Array.from(abortControllerRef.current?.keys() || []);

      await Promise.all(
        targetIds.map(async (id) => {
          abortControllerRef.current?.get(id)?.abort();
          await DocumentService.cancelUpload(id);
          await uploadProgressHub.leaveGroup(id);
        })
      );

      const remaining = activeUploads.filter(
        (u) => !targetIds.includes(u.FileOperationId)
      );

      if (remaining.length === 0) {
        disable();
      } else {
        setActiveUploads(remaining);
      }
    } catch (error) {
      console.error("Cancel upload failed", error);
    }
  };

  function disable() {
    uploadProgressHub.leaveGroups([...contextOperationIds]).then(() => uploadProgressHub.stop());
    hasProcessedSuccess.current = false;
    hasTriggeredUploadCloseRef.current = false;
    setUploads([]);
    setIsMinimized(false);
    setIsCompleteApiDone(false);
    setUploadRoute("");
    setContextOperationIds([]);
    setContextFileNameMap({});
    setContextFileVersionMap({});
    unsubscribeHubRef.current?.();
    unsubscribeHubRef.current = null;
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (hubFallbackTimerRef.current) {
      clearTimeout(hubFallbackTimerRef.current);
      hubFallbackTimerRef.current = null;
    }
    uploadQueueRef.current = {};
    props.hideModal();
    setModal(false);
    setError(false);
    setIsValidating(false);
    setActiveUploads([]);
    !props.copy && props.setMultipleFiles && props.setMultipleFiles([]);
    setFileNameError(false);
    setNameError(false);
    setFileNameError(false);
    setAddDocument(false);
    setNewVersion(false);
    setMsg("");
    setUpload(false);
    setSingleFileError(false);
    setDocumentItem(null);
    setNewFileName(props.document?.rootDocumentName);
    setFolderName(props?.folderName);
    setProjectId(props?.projectId);
    props?.setRootDocumentId && props?.setRootDocumentId(0);
  }

  const Response = (data: any) => {
    return (
      <Stack className={mergeStyles(styles.responseContainer)}>
        {data.map((res: any) => {
          const code = res.code !== undefined ? res.code : res.Code;
          const message = res.message !== undefined ? res.message : res.Message;
          return code === 201 || code === 200 ? (
            <Stack className={mergeStyles(styles.success)} key={message}>
              <Stack style={{ alignItems: "center", paddingRight: "15px" }}>
                <SuccessIcon />
              </Stack>
              <Stack>
                <TextForBody className={mergeStyles(styles.successMessage)}>
                  {message}
                </TextForBody>
              </Stack>
            </Stack>
          ) : (
            <Stack className={mergeStyles(styles.failed)} key={message}>
              <Stack style={{ alignItems: "center", paddingRight: "15px" }}>
                <FontIcon
                  iconName="StatusErrorFull"
                  style={{
                    fontSize: "25px",
                    backgroundColor: colorPallete.white,
                    color: colorPallete.btRed,
                  }}
                />
              </Stack>
              <Stack>
                <TextForBody className={mergeStyles(styles.errorMessage)}>
                  {message} :{" "}
                  {code === 208
                    ? t("already_exists")
                    : code === 404
                      ? t("file_empty")
                      : code === 500 && t("upload_fail")}
                </TextForBody>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    );
  };

  const onChangeFileNameTextField = useCallback(
    (
      event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue?: string
    ) => {
      setNewFileName(newValue || "");
    },
    []
  );

  function isReadyToSubmit() {
    if (String(folderName).length === 0 || error1 || fileNameError) return false;
    if (props.copy) {
      return !newVersion || String(documentName).length > 0;
    }
    if (!props.multipleFiles || props.multipleFiles.length === 0) return false;
    if (hasDuplicateFileNames) return false;
    const newVersionFilesWithoutDoc = props.multipleFiles.filter(
      (f: BtFileObject) => f.isNewVersion && (!f.rootDocumentId || f.rootDocumentId === 0)
    );
    return newVersionFilesWithoutDoc.length === 0;
  }

  function getBackgroundColor() {
    return isReadyToSubmit() ? `${colorPallete.btBlue}` : "#EAEAEA";
  }

  function getFontColor() {
    return isReadyToSubmit() ? `${colorPallete.white}` : "#717070";
  }

  function isButtonDisabled() {
    return !isReadyToSubmit();
  }

  const ErrorJSX = (props: any) => {
    return (
      <Stack
        style={{ width: "100%", alignItems: "flex-start", marginTop: "4px" }}
      >
        <Text
          style={{
            ...styles.mediumText,
            color: colorPallete.btRed,
          }}
        >
          {props?.message}
        </Text>
      </Stack>
    );
  };

  const documentOptions = useMemo(
    () =>
      Array.isArray(docsData) && docsData.length > 0
        ? docsData.filter((doc: any) => doc && doc.rootDocumentName)
        : [],
    [docsData]
  );

  return (
    <FormModal
      title={props.copy ? t("copy_precedent") : t("import_documents")}
      TitleBackgroundColor={"linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)"}
      isModalOpen={props.isModalOpen}
      showModal={props.showModal}
      hideModal={props.hideModal}
      fileName={props.fileName}
      TitleColor={colorPallete.black1}
      backgroundColor="linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)"
      width="610px"
      cancelButton
      customCancel={disable}
      disableScroll={true}
    >
      {isDraggingOver && !props.copy && <DragOverlay />}
      <div
        className="createDocumentModal"
        style={{
          background: "linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)",
          padding: "10px",
        }}
      >
        {modal ? (
          <Stack className={mergeStyles(styles.uploadModal)}>
            {upload ? (
              showResponse ? (
                Response(responseData)
              ) : (
                <>
                  <TextForPageHeaders>{t(msg)}</TextForPageHeaders>
                </>
              )
            ) : (
              <Stack style={styles.loader}>
                {props.copy ? (
                  <>
                    <Stack style={{ padding: "2%" }}>
                      <Loader />
                    </Stack>
                    <TextForPageHeaders>{t("copying_docs")}</TextForPageHeaders>
                  </>
                ) : (
                  <Stack style={{ width: "100%", gap: 8 }}>
                    {isValidating && (
                      <HorizontalStack style={styles.validatingRow}>
                        <Loader />
                        <Text style={{ ...styles.mediumText, marginLeft: "10px" }}>
                          {t("Validating files")}
                        </Text>
                      </HorizontalStack>
                    )}
                    {activeUploads.map((u) => (
                      <Stack key={u.FileOperationId} style={styles.fileUploadRow}>
                        <HorizontalStack
                          style={{
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "6px",
                          }}
                        >
                          <Text style={{ ...styles.mediumText, maxWidth: "430px" }}>
                            {fileNameMapRef.current[u.FileOperationId] ?? u.FileOperationId}
                          </Text>
                          <Text style={styles.mediumText}>{u.Percent}%</Text>
                        </HorizontalStack>
                        <HorizontalStack
                          style={{ alignItems: "center", width: "100%", gap: "8px" }}
                        >
                          <Stack style={styles.fileProgressTrack}>
                            <Stack
                              style={{
                                ...styles.fileProgressFill,
                                width: `${Math.max(0, Math.min(100, u.Percent))}%`,
                              }}
                            />
                          </Stack>
                          {u.Stage !== "uploading" && (
                            <Text style={{ ...styles.mediumText, minWidth: "64px", textAlign: "right" }}>
                              {t(u.Stage)}
                            </Text>
                          )}
                          {isActiveStage(u.Stage) && (
                            <FontIcon
                              iconName="Cancel"
                              style={styles.cancelFileIcon}
                              onClick={() => cancelUpload(u.FileOperationId)}
                            />
                          )}
                        </HorizontalStack>
                      </Stack>
                    ))}
                    {!isCompleteApiDone && activeUploads.length > 0 && (
                      <HorizontalStack style={styles.uploadWarningRow}>
                        <FontIcon iconName="Warning" style={styles.uploadWarningIcon} />
                        <Text style={styles.uploadWarningText}>
                          Closing or refreshing this tab may stop the upload process.
                        </Text>
                      </HorizontalStack>
                    )}
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
        ) : (
          <>
            {addDocument ? (
              <>
                <AddNewVersion
                  documentName={documentName}
                  folderName={folderName}
                  addDocument={addDocument}
                  setAddDocument={setAddDocument}
                  multipleFiles={props.multipleFiles}
                  rootDocumentId={rootDocumentId}
                  handleCLick={handleClick}
                  setDocumentName={setDocumentName}
                  fileName={props.fileName}
                />
              </>
            ) : (
              <>
                <VerticalStack style={styles.container}>
                  <HorizontalStack className="projectName">
                    <Label style={styles.headingText} required>
                      {t("project_name")}
                    </Label>
                    {projectLimit !== 0 && (
                      <Text
                        onClick={() => {
                          props.project();
                        }}
                        style={styles.blueText}
                      >
                        + {t("new_project")}
                      </Text>
                    )}
                  </HorizontalStack>
                  <CustomDropdown
                    options={projectData.map((item: any) => item.name)}
                    title={t("project_dd")}
                    data={setFolderName}
                    error={setFolderNameError}
                    folderName={folderName}
                  />
                  {folderNameError ? (
                    <ErrorJSX message={t("error_project")} />
                  ) : null}

                  <VerticalStack style={{ marginTop: "28px", minHeight: "237px" }}>
                    {props?.copy ? (
                      <>
                        <VerticalStack style={{ marginBottom: "28px" }}>
                          <HorizontalStack style={{ ...styles.documentCard }}>
                            {props.document?.metadata?.fileType === "pdf" ? (
                              <PdfIcon />
                            ) : (
                              <WordDocIcon />
                            )}
                            {!newVersion ? (
                              <TooltipHost
                                content={"File name cannot contain any of these characters: \\ / : * ? < > |"}
                                styles={tooltipHostStyles}
                                calloutProps={{ isBeakVisible: true, beakWidth: 10, gapSpace: 5 }}
                              >
                                <TextField
                                  borderless
                                  id="fileName"
                                  value={newFileName}
                                  onChange={onChangeFileNameTextField}
                                />
                              </TooltipHost>
                            ) : (
                              <Text style={styles.documentText}>
                                {typeof props.document?.rootDocumentName === "string"
                                  ? props.document.rootDocumentName
                                  : ""}
                              </Text>
                            )}
                          </HorizontalStack>

                          <HorizontalStack style={{ gap: "8px", marginTop: "8px" }}>
                            <HorizontalStack
                              onClick={() => {
                                setNewVersion(false);
                                setDocumentName("");
                                setRootDocumentId(0);
                                setDocumentItem(null);
                              }}
                              style={{
                                ...styles.toggleButton,
                                border: !newVersion
                                  ? `1px solid ${colorPallete.btBlue}`
                                  : "1px solid #D0D0D0",
                              }}
                            >
                              <Stack
                                style={{
                                  ...styles.toggleRadio,
                                  border: !newVersion ? "1px solid #18468F" : "1px solid #707070",
                                }}
                              >
                                {!newVersion && <Stack style={styles.toggleRadioFill} />}
                              </Stack>
                              <Text style={styles.toggleLabel}>{t("new_document")}</Text>
                            </HorizontalStack>

                            <HorizontalStack
                              onClick={() => setNewVersion(true)}
                              style={{
                                ...styles.toggleButton,
                                border: newVersion
                                  ? `1px solid ${colorPallete.btBlue}`
                                  : "1px solid #D0D0D0",
                              }}
                            >
                              <Stack
                                style={{
                                  ...styles.toggleRadio,
                                  border: newVersion ? "1px solid #18468F" : "1px solid #707070",
                                }}
                              >
                                {newVersion && <Stack style={styles.toggleRadioFill} />}
                              </Stack>
                              <Text style={styles.toggleLabel}>{t("new_version")}</Text>
                            </HorizontalStack>
                          </HorizontalStack>

                          {newVersion &&
                            String(folderName).length > 0 &&
                            documentOptions.length > 0 && (
                              <Stack style={{ marginTop: "8px" }}>
                                <CustomDropdown
                                  options={documentOptions}
                                  title={t("document_dd")}
                                  data={setDocumentName}
                                  isDocumentDropdown={true}
                                  folderName={folderName}
                                  error={setDocumentNameError}
                                  documentId={setRootDocumentId}
                                  rootDocumentId={rootDocumentId}
                                  documentItem={documentItem}
                                  setDocumentItem={setDocumentItem}
                                />
                                {documentNameError && <ErrorJSX message={t("error_docfamily")} />}
                              </Stack>
                            )}
                        </VerticalStack>
                      </>
                    ) : (
                      // — flujo upload (no copy) —
                      <VerticalStack style={{ marginBottom: "28px" }}>
                        <Stack
                          style={{
                            ...styles.baseStyle,
                            backgroundColor: error1 ? "#F6EAE7" : "#F8F8F8",
                          }}
                        >
                          <div {...getRootProps({ style })}>
                            <input {...getInputProps()} />
                            <Stack
                              style={{
                                flexDirection: props.multipleFiles.length > 0 ? "row" : "column",
                                alignItems: "center",
                                gap: "8px",
                                justifyContent: "center",
                                padding: props.multipleFiles.length > 0 ? "7px 0px" : "15px 0px",
                              }}
                            >
                              <UploadDocumentIcon
                                height={props.multipleFiles.length > 0 ? "16px" : "34px"}
                                width={props.multipleFiles.length > 0 ? "15px" : "33px"}
                              />
                              <HorizontalStack>
                                <Text style={styles.dragText}>{t("drag_drop")}</Text>
                                <Link
                                  style={{
                                    fontSize: "0.8rem",
                                    fontWeight: "400",
                                    color: colorPallete.btBlue,
                                    fontFamily: "Poppins",
                                  }}
                                  underline
                                >
                                  {t("browse")}
                                </Link>
                              </HorizontalStack>
                            </Stack>
                          </div>
                        </Stack>

                        {props.multipleFiles.length === 0 ? (
                          error1 && <ErrorJSX message={t("error_document")} />
                        ) : (
                          <TooltipHost
                            content={"File name cannot contain any of these characters: \\ / : * ? < > |"}
                            styles={tooltipHostStyles}
                            calloutProps={{ isBeakVisible: true, beakWidth: 10, gapSpace: 5 }}
                          >
                            <DocumentList
                              multipleFiles={props.multipleFiles}
                              setMultipleFiles={props.setMultipleFiles}
                              // newVersion={newVersion}
                              docsData={documentOptions}
                              invalidFiles={invalidOrDuplicateFiles}
                              onFileNameChange={validateFileName}
                              onFileTypeChange={handleFileTypeChange}
                            />
                          </TooltipHost>
                        )}

                        {fileNameError && <ErrorJSX message={fileNameErrorMessage} />}
                        {nameError && <ErrorJSX message={t("error_filename")} />}
                        {duplicateFileNames.inProject.map((fileName) => (
                          <DuplicateNameError key={`exists-${fileName}`} fileName={fileName} reason="exists" />
                        ))}
                        {duplicateFileNames.inBatch.map((fileName) => (
                          <DuplicateNameError key={`repeated-${fileName}`} fileName={fileName} reason="repeated" />
                        ))}
                      </VerticalStack>
                    )}
                  </VerticalStack>
                </VerticalStack>
                <Stack className="horizontalLine"></Stack>
                <HorizontalStack style={styles.bottom}>
                  <Text
                    style={{
                      ...styles.addButton,
                      ...styles.mediumText,
                      fontWeight: "500",
                      background: getBackgroundColor(),
                      color: getFontColor(),
                      textAlign: "center",
                      cursor: isButtonDisabled() ? "not-allowed" : "pointer",
                      opacity: isButtonDisabled() ? 0.7 : 1,
                      border: isButtonDisabled() ? "1px solid #D3D3D3" : `1px solid ${colorPallete.btBlue}`,
                      pointerEvents: isButtonDisabled() ? "none" : "auto"
                    }}
                    className="addButton"
                    onClick={() => !isButtonDisabled() && handleClick("")}
                  >
                    {props.copy ? t("copy") : t("import")}
                  </Text>
                  <Text
                    className="cancelButton"
                    style={{
                      ...styles.cancelButton,
                      ...styles.mediumText,
                      textAlign: "center",
                      fontWeight: "500",
                    }}
                    onClick={disable}
                  >
                    {t("cancel")}
                  </Text>
                </HorizontalStack>
              </>
            )}
          </>
        )}
      </div>
    </FormModal>
  );
};

export const styles = {
  container: {
    paddingLeft: "25px",
    paddingRight: "25px",
    backgroundColor: "white",
    paddingTop: "30px",
    borderRadius: "8px 8px 0 0",
  },
  headingText: {
    color: "#1F2430",
    fontFamily: "Poppins",
    fontSize: "16px",
    fontWeight: "500",
    lineHeight: "24px",
    fontStyle: "normal",
  },
  mediumText: {
    color: "#323232",
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: "400",
    lineHeight: "16px",
    fontStyle: "normal",
  },
  documentText: {
    color: "#323232",
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: "400",
    lineHeight: "16px",
    fontStyle: "normal",
    background: "#F8F8F8",
    width: "470px",
  },
  blueText: {
    color: `${colorPallete.btBlue}`,
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "24px",
    fontStyle: "normal",
    cursor: "pointer",
  },
  addButton: {
    padding: "12px 24px",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    borderRadius: "4px",
    cursor: "pointer",
    width: "120px",
  },
  cancelButton: {
    padding: "12px 24px",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    borderRadius: "4px",
    backgroundColor: "#E8ECF4",
    color: "#323232",
    cursor: "pointer",
    width: "120px",
  },
  fileUploadRow: {
    border: "1px solid #E0E0E0",
    borderRadius: "6px",
    padding: "8px 10px",
    background: "#FFFFFF",
  },
  fileProgressTrack: {
    flex: 1,
    height: "8px",
    borderRadius: "999px",
    background: "#E8ECF4",
    overflow: "hidden",
  },
  fileProgressFill: {
    height: "100%",
    borderRadius: "999px",
    background: colorPallete.btBlue,
    transition: "width 0.3s ease",
  },
  cancelFileIcon: {
    color: colorPallete.btBlue,
    fontSize: "12px",
    cursor: "pointer",
    minWidth: "16px",
  },
  validatingRow: {
    width: "100%",
    padding: "10px 14px",
    borderRadius: "6px",
    border: "1px solid #E8ECF4",
    background: "#F8F8F8",
    alignItems: "center",
    gap: "0px",
  },
  bottom: {
    padding: "20px 25px",
    alignSelf: "flex-end",
    gap: "9px",
    justifyContent: "flex-end",
    backgroundColor: "white",
    borderRadius: "0 0 8px 8px",
  },
  dragText: {
    paddingRight: "0.3rem",
    fontSize: "0.8rem",
    fontWeight: "400",
    color: colorPallete.grey1,
    fontFamily: "Poppins",
  },
  versionCard: {
    padding: "8px 16px",
    borderRadius: "4px",
    border: `1px solid ${colorPallete.btLightBlue}`,
    backgroundColor: "white",
    alignItems: "flex-start",
    justifyContent: "center",
    gap: "10px",
    width: "98%",
    minHeight: "60px",
  },
  versionContainer: {
    width: "91.5%",
    borderRadius: "8px",
    border: "1px solid #EAEAEA",
    backgroundColor: "#F8F8F8",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    alignSelf: "flex-end",
  },
  projectDetails: {
    alignItems: "center",
    gap: "10px",
  },
  projectText: {
    color: "#323232",
    fontFamily: "Poppins",
    fontSize: "17px",
    fontWeight: "500",
    lineHeight: "18px",
    fontStyle: "normal",
  },
  loader: {
    padding: "2%",
    backgroundColor: "",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
    alignSelf: "center",
    marginTop: "1%",
  },
  versionHistory: {
    backgroundColor: "white",
    padding: "20px 25px",
    borderRadius: "8px",
  },
  responseContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: colorPallete.white,
    height: "100%",
    padding: "15px",
  },
  success: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    backgroundColor: colorPallete.white,
    paddingBottom: "2%",
  },
  failed: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    backgroundColor: colorPallete.white,
    paddingBottom: "2%",
  },
  baseStyle: {
    backgroundColor: "#F8F8F8",
    borderRadius: "8px",
    padding: "10px",
    cursor: "pointer",
    justifyContent: "center",
    alignItems: "center",
    backgroundImage: `repeating-linear-gradient(1deg, #51596C, #51596C 11px, transparent 11px, transparent 25px, #51596C 25px), repeating-linear-gradient(91deg, #51596C, #51596C 11px, transparent 11px, transparent 25px, #51596C 25px), repeating-linear-gradient(181deg, #51596C, #51596C 11px, transparent 11px, transparent 25px, #51596C 25px), repeating-linear-gradient(271deg, #51596C, #51596C 11px, transparent 11px, transparent 25px, #51596C 25px)`,
    backgroundSize: "1px 100%, 100% 1px, 1px 100% , 100% 1px",
    backgroundPosition: "0 0, 0 0, 100% 0, 0 100%",
    backgroundRepeat: "no-repeat",
    display: "flex",
    marginBottom: "8px",
  },
  focusedStyle: {
    borderColor: "#2196f3",
  },
  acceptStyle: {
    borderColor: "#0E4394",
  },
  rejectStyle: {
    borderColor: "#ff1744",
  },
  uploadModal: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
    minHeight: "8rem",
    alignItems: "center",
    padding: "1%",
    backgroundColor: colorPallete.white,
    borderRadius: "8px",
  },
  errorMessage: {
    color: colorPallete.btRed,
    fontSize: "14px",
    fontfamily: "Poppins",
  },
  successMessage: {
    color: colorPallete.btGreen,
    fontSize: "14px",
    fontfamily: "Poppins",
  },
  documentCard: {
    width: "100%",
    padding: "6px 16px",
    gap: "16px",
    borderRadius: "5px",
    border: "1px solid #EAEAEA",
    background: "#F8F8F8",
    alignItems: "center",
  },
  toggleButton: {
    flex: 1,
    height: "36px",
    backgroundColor: "#F8F8F8",
    alignItems: "center" as const,
    borderRadius: "5px",
    cursor: "pointer",
    gap: "6px",
  },
  toggleRadio: {
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    alignItems: "center" as const,
    justifyContent: "center" as const,
    marginLeft: "12px",
    position: "relative" as const,
    flexShrink: 0,
  },
  toggleRadioFill: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: "#18468F",
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  toggleLabel: {
    fontFamily: "Poppins",
    fontSize: "13px",
    fontWeight: "400" as const,
    color: "#323232",
    lineHeight: "16px",
  },
  uploadWarningRow: {
    alignItems: "center",
    gap: "6px",
    marginTop: "6px",
    padding: "6px 10px",
    borderRadius: "4px",
    background: "#FFF8EC",
    border: "1px solid #F5C842",
  },
  uploadWarningIcon: {
    color: "#C8872A",
    fontSize: "13px",
    flexShrink: 0,
  } as React.CSSProperties,
  uploadWarningText: {
    color: "#7A5100",
    fontFamily: "Poppins",
    fontSize: "12px",
    fontWeight: "400",
    lineHeight: "18px",
  },
};
